import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Check,
  ChevronDown,
  ChevronUp,
  GripVertical,
  LayoutDashboard,
  Loader2,
  Plus,
  Shield,
  X,
} from 'lucide-react';
import { USER_ROLE_LABELS } from '@hygge/shared';
import type { AvailableWidget, RoleWithPermissions } from '@hygge/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/errors';
import { useRoles } from '@/hooks/use-roles-permissions';
import { useRoleDashboardWidgets, useUpdateRoleDashboardWidgets } from '@/hooks/use-dashboard';
import { DASHBOARD_WIDGET_COMPONENTS } from './widget-registry';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
const roleLabel = (role: RoleWithPermissions) => USER_ROLE_LABELS[role.name] || role.name;

/**
 * A catalog widget with no built frontend component yet (e.g. `teamTasksDueSoon`, blocked on
 * the Task Manager module). It can't render on a dashboard, so the Configurator marks it
 * "Coming soon" and does not let it be assigned.
 */
const isComingSoon = (key: string) => !(key in DASHBOARD_WIDGET_COMPONENTS);

function ComingSoonBadge() {
  return (
    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      Coming soon
    </span>
  );
}

/**
 * Marks an `hr`-scoped widget. These show company-wide HR data (absences incl. type, headcount,
 * probation/review dates), so assigning one to a broad role exposes that data to everyone on
 * the role. A heads-up for whoever curates the config — NOT a hard gate; assignment is still
 * the only control. Requires a `TooltipProvider` ancestor (the pane wraps one).
 */
function HrScopeBadge() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="shrink-0 cursor-help rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
          HR · company-wide
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px]">
        Shows company-wide HR data (absences, headcount, probation/review dates). Assign only to
        HR-appropriate roles.
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * A truncating (single-line ellipsis) text span that shows a tooltip with the full text on
 * hover — but ONLY when the text is actually clipped. Measures overflow and re-checks on
 * resize. Requires a `TooltipProvider` ancestor.
 */
function TruncatedText({ text, className }: { text: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span ref={ref} className={className}>
          {text}
        </span>
      </TooltipTrigger>
      {truncated && <TooltipContent side="top">{text}</TooltipContent>}
    </Tooltip>
  );
}

/**
 * Dashboard Configurator — per real RBAC role, choose which widgets that role's dashboard
 * shows and in what order. Any widget can be assigned to any role; the whole catalog (minus
 * already-assigned) is offered.
 */
export function DashboardConfiguratorPage() {
  const { data: roles, isLoading } = useRoles();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = roles?.find((r) => r.id === selectedId) ?? roles?.[0] ?? null;

  // Switching role tabs remounts ConfiguratorPane (keyed by role id), which drops any
  // unsaved draft. Track the active pane's dirty state so a tab switch that would discard
  // edits asks for confirmation first, instead of silently losing them.
  const dirtyRef = useRef(false);
  const handleDirtyChange = useCallback((d: boolean) => {
    dirtyRef.current = d;
  }, []);
  const [pendingRoleId, setPendingRoleId] = useState<string | null>(null);

  const requestSelect = (roleId: string) => {
    if (roleId === selected?.id) return;
    if (dirtyRef.current) {
      setPendingRoleId(roleId); // open the discard-confirmation dialog
    } else {
      setSelectedId(roleId);
    }
  };

  const confirmSwitch = () => {
    if (pendingRoleId) setSelectedId(pendingRoleId);
    setPendingRoleId(null);
  };

  // Fill the viewport height below the settings header so only the widget lists scroll —
  // the page header, role tabs, and the Save/Discard bar stay static. Measured at runtime
  // (and on resize) rather than a hardcoded calc() offset, which the multi-row settings
  // header and the impersonation banner would throw off.
  const rootRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState<number | undefined>();
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    // rect.top is viewport-relative (scrollY already subtracted); add scrollY back so the
    // measurement is the element's document offset — stable regardless of the current scroll
    // position when this mounts (e.g. switching here from a scrolled-down sibling settings tab).
    const measure = () =>
      setAvailableHeight(window.innerHeight - (el.getBoundingClientRect().top + window.scrollY));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <div
      ref={rootRef}
      style={availableHeight && availableHeight > 0 ? { height: availableHeight } : undefined}
      className="mx-auto flex max-w-7xl flex-col gap-6 overflow-hidden p-6"
    >
      <div className="flex shrink-0 items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <LayoutDashboard className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Dashboard Configurator
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Choose which widgets each role sees on their dashboard, and in what order.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      ) : !roles?.length ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Shield className="mb-4 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No roles found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          {/* Role selector — horizontal segmented tabs (one per real RBAC role): a single
              bordered row that scrolls horizontally when the roles overflow. */}
          <div className="flex shrink-0 gap-1 overflow-x-auto rounded-lg border border-border bg-muted/40 p-1">
            {roles.map((role) => {
              const active = selected?.id === role.id;
              return (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => requestSelect(role.id)}
                  className={cn(
                    'cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    active
                      ? 'bg-cta text-cta-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {roleLabel(role)}
                </button>
              );
            })}
          </div>

          {selected && (
            <ConfiguratorPane key={selected.id} role={selected} onDirtyChange={handleDirtyChange} />
          )}
        </div>
      )}

      <AlertDialog
        open={pendingRoleId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRoleId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved widget changes for this role. Switching to another role will
              discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSwitch}>Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ConfiguratorPane({
  role,
  onDirtyChange,
}: {
  role: RoleWithPermissions;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { data, isError, error } = useRoleDashboardWidgets(role.id);
  const update = useUpdateRoleDashboardWidgets();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const [assigned, setAssigned] = useState<AvailableWidget[]>([]);
  const [available, setAvailable] = useState<AvailableWidget[]>([]);
  const [ready, setReady] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const seededKeysRef = useRef<string | null>(null);

  // Seed the local draft on first load and whenever the SERVER's assignment actually changes
  // (e.g. after our own save). A background refetch that returns identical server state is
  // ignored, so in-progress unsaved edits are never silently clobbered.
  useEffect(() => {
    if (!data) return;
    const serverKeys = data.assigned.map((a) => a.key).join(',');
    if (seededKeysRef.current === serverKeys) return;
    seededKeysRef.current = serverKeys;
    setAssigned(data.assigned.map((a) => ({ key: a.key, name: a.name, scope: a.scope })));
    setAvailable([...data.available].sort((a, b) => a.name.localeCompare(b.name)));
    setReady(true);
  }, [data]);

  // Whether the local draft diverges from the server's assignment. Computed here — before the
  // loading/error early-returns below — so this hook and the effect keep a stable order, and
  // reported up so the parent can confirm before a role-tab switch discards unsaved edits.
  const dirty =
    !!data && data.assigned.map((a) => a.key).join(',') !== assigned.map((a) => a.key).join(',');
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  // Error before the skeleton — otherwise a failed initial load (no data, not loading) would
  // sit on `!ready` forever and never reach the error branch.
  if (isError && !data) {
    return (
      <div className="p-6 text-sm text-destructive">
        {extractErrorMessage(error, "Couldn't load this role's widgets.")}
      </div>
    );
  }

  if (!data || !ready) {
    return (
      <div className="space-y-3 p-5">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const addWidget = (w: AvailableWidget) => {
    setAvailable((prev) => prev.filter((x) => x.key !== w.key));
    setAssigned((prev) => [...prev, w]);
  };

  const removeWidget = (w: AvailableWidget) => {
    setAssigned((prev) => prev.filter((x) => x.key !== w.key));
    setAvailable((prev) => [...prev, w].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const moveWidget = (index: number, dir: -1 | 1) => {
    setAssigned((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setAssigned((prev) => {
      const oldIdx = prev.findIndex((w) => w.key === active.id);
      const newIdx = prev.findIndex((w) => w.key === over.id);
      if (oldIdx === -1 || newIdx === -1) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const discard = () => {
    setSaveError(null);
    setAssigned(data.assigned.map((a) => ({ key: a.key, name: a.name, scope: a.scope })));
    setAvailable([...data.available].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const save = async () => {
    setSaveError(null);
    try {
      await update.mutateAsync({ roleId: role.id, widgetKeys: assigned.map((a) => a.key) });
    } catch (err) {
      setSaveError(extractErrorMessage(err, 'Failed to save dashboard widgets'));
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Header (static) */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-lg font-semibold tracking-tight text-foreground">
              {roleLabel(role)}
            </h2>
          </div>
        </div>
      </div>

      {saveError && <p className="text-sm text-destructive">{saveError}</p>}

      {/* Two panes — fill the remaining height; each list scrolls internally when it
          overflows, while the pane headers and the action bar stay put. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        {/* Available */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-muted/20">
          <div className="shrink-0 px-4 pb-2.5 pt-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Available widgets
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3.5">
            {available.length === 0 ? (
              <p className="py-6 text-center text-xs text-muted-foreground">
                All widgets added.
              </p>
            ) : (
              available.map((w) => (
                <div
                  key={w.key}
                  className="flex items-center gap-2 border-b border-border/60 py-2 last:border-0"
                >
                  <TruncatedText
                    text={w.name}
                    className="min-w-0 flex-1 truncate text-sm text-foreground"
                  />
                  {w.scope === 'hr' && <HrScopeBadge />}
                  {isComingSoon(w.key) ? (
                    <ComingSoonBadge />
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 cursor-pointer px-2 text-xs"
                      onClick={() => addWidget(w)}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Add
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Assigned */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-muted/20">
          <div className="shrink-0 px-4 pb-2.5 pt-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            This role&rsquo;s dashboard <span className="font-normal">· shown in this order</span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3.5">
            {assigned.length === 0 ? (
              <p className="py-6 text-center text-xs text-muted-foreground">
                No widgets assigned yet.
              </p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={assigned.map((w) => w.key)}
                  strategy={verticalListSortingStrategy}
                >
                  {assigned.map((w, i) => (
                    <SortableAssignedRow
                      key={w.key}
                      widget={w}
                      index={i}
                      total={assigned.length}
                      onMove={moveWidget}
                      onRemove={removeWidget}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>

      {/* Save / Discard — static footer, always visible at the bottom of the fixed-height pane. */}
      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border pt-3">
        {dirty && <span className="mr-auto text-xs text-muted-foreground">Unsaved changes</span>}
        <Button
          variant="ghost"
          size="sm"
          onClick={discard}
          disabled={!dirty || update.isPending}
          className="cursor-pointer"
        >
          Discard
        </Button>
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || update.isPending}
          className="cursor-pointer"
        >
          {update.isPending ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-1.5 h-4 w-4" />
          )}
          Save
        </Button>
      </div>
    </div>
    </TooltipProvider>
  );
}

/** One draggable assigned-widget row: drag handle (⠿) + up/down arrows + remove. */
function SortableAssignedRow({
  widget,
  index,
  total,
  onMove,
  onRemove,
}: {
  widget: AvailableWidget;
  index: number;
  total: number;
  onMove: (index: number, dir: -1 | 1) => void;
  onRemove: (widget: AvailableWidget) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: widget.key,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 border-b border-border/60 bg-background py-2 last:border-0"
    >
      <div
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        title="Drag to reorder"
        className="shrink-0 cursor-grab touch-none text-muted-foreground opacity-40 transition-opacity hover:opacity-100 active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <TruncatedText text={widget.name} className="min-w-0 flex-1 truncate text-sm text-foreground" />
      {widget.scope === 'hr' && <HrScopeBadge />}
      {isComingSoon(widget.key) && <ComingSoonBadge />}
      <div className="flex flex-col">
        <button
          type="button"
          aria-label="Move up"
          disabled={index === 0}
          onClick={() => onMove(index, -1)}
          className="cursor-pointer text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          aria-label="Move down"
          disabled={index === total - 1}
          onClick={() => onMove(index, 1)}
          className="cursor-pointer text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
      <button
        type="button"
        aria-label={`Remove ${widget.name}`}
        onClick={() => onRemove(widget)}
        className="cursor-pointer text-muted-foreground hover:text-destructive"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
